import React, { useEffect, useRef } from 'react';

type Props = {
	patternSize?: number;
	patternScaleX?: number;
	patternScaleY?: number;
	patternRefreshInterval?: number;
	patternAlpha?: number;
	percentage?: number;
	children?: React.ReactNode;
};

export function VideoGrain({
	patternSize = 250,
	patternScaleX = 1,
	patternScaleY = 1,
	patternRefreshInterval = 2,
	patternAlpha = 35,
	percentage = 100,
	children,
}: Props) {
	const grainRef = useRef<HTMLCanvasElement | null>(null);
	const _raw = (percentage - 50) / 50;
	const _eased = Math.pow(Math.max(0, _raw), 0.8);
	const intensity = Math.max(0, Math.min(1, _eased));
	const displayIntensity = intensity * 0.25;

	const percentageRef = useRef<number>(percentage);
	const patternAlphaRef = useRef<number>(patternAlpha);

	useEffect(() => {
		percentageRef.current = percentage;
	}, [percentage]);

	useEffect(() => {
		patternAlphaRef.current = patternAlpha;
	}, [patternAlpha]);

	useEffect(() => {
		const canvas = grainRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d', { alpha: true });
		if (!ctx) return;

		let frame = 0;
		let animationId: number;

		const canvasSize = 1024;

		const resize = () => {
			canvas.width = canvasSize;
			canvas.height = canvasSize;

			canvas.style.width = '100vw';
			canvas.style.height = '100vh';
		};

		const drawGrain = () => {
			const rawLocal = (percentageRef.current - 50) / 50;
			const intensityLocal = Math.max(0, Math.min(1, Math.pow(Math.max(0, rawLocal), 0.8)));
			const alphaByte = Math.max(0, Math.min(255, Math.round(patternAlphaRef.current * intensityLocal)));

			if (alphaByte === 0) {
				ctx.clearRect(0, 0, canvasSize, canvasSize);
				return;
			}

			const imageData = ctx.createImageData(canvasSize, canvasSize);
			const data = imageData.data;

			for (let i = 0; i < data.length; i += 4) {
				const value = Math.random() * 255;
				data[i] = value;
				data[i + 1] = value;
				data[i + 2] = value;
				data[i + 3] = alphaByte;
			}

			ctx.putImageData(imageData, 0, 0);
		};

		const loop = () => {
			if (frame % patternRefreshInterval === 0) {
				drawGrain();
			}
			frame++;
			animationId = window.requestAnimationFrame(loop);
		};

		window.addEventListener('resize', resize);
		resize();
		loop();

		return () => {
			window.removeEventListener('resize', resize);
			window.cancelAnimationFrame(animationId);
		};
	}, [patternSize, patternScaleX, patternScaleY, patternRefreshInterval]);

	return (
		<div className="relative overflow-hidden">
			<canvas ref={grainRef} className="pointer-events-none fixed inset-0 -z-20" style={{ imageRendering: 'pixelated' }} />
			<div
				className="pointer-events-none fixed inset-0 -z-10 transition-opacity duration-300 ease-linear"
				style={{
					opacity: displayIntensity,
					filter: `blur(${Math.max(0, 20 * displayIntensity)}px)`,
					background: `
						linear-gradient(to bottom, rgba(255,0,0,0.9) 0%, rgba(255,0,0,0) 45%),
						linear-gradient(to top, rgba(255,0,0,0.9) 0%, rgba(255,0,0,0) 45%),
						linear-gradient(90deg, rgba(255,0,0,0.9) 0%, rgba(255,0,0,0) 20%),
						linear-gradient(270deg, rgba(255,0,0,0.9) 0%, rgba(255,0,0,0) 20%)
					  `,
				}}
			/>
			{children}
		</div>
	);
}
